import React, { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '../components/ui/card'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table'
import { Button } from '../components/ui/button'
import { format, isValid, parseISO } from 'date-fns'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '../components/ui/sheet'

import { Input } from '../components/ui/input'
import EditUser from '../components/EditUser'
import NewUser from '../components/NewUser'
import { ClipLoader } from 'react-spinners'
import Paginator from '../components/Paginator'
import { useDeleteUser, useGetAllUsers } from '../migration-api/api-hooks/users'
import { useToast } from '../components/ui/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog'
import { size } from 'lodash'

function User () {
  const [open, setOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [user, setUser] = useState({})
  const [searchKey, setSearchKey] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [pageSize, setPageSize] = useState(10)

  const { toast } = useToast()
  const {
    data: allUsers,
    isLoading,
    refetch
  } = useGetAllUsers({
    page: currentPage,
    size: pageSize
  })

  // Delete user mutation
  const { deleteUser, isLoading: isDeleting } = useDeleteUser({
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Success',
        description: 'User deleted successfully'
      })
      setDeleteDialogOpen(false)
      setUserToDelete(null)
      // Refetch users after deletion
      refetch()
    },
    onError: error => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to delete user'
      })
    }
  })

  const onPageChange = page => {
    setCurrentPage(page)
    setIsSearching(false)
    setSearchKey('')
  }

  function handleDate (date) {
    if (date == null) return 'N/A'
    const validDate = parseISO(date, 'yyyy-MM-dd', new Date())
    if (isValid(validDate)) return format(new Date(date), 'yyyy-MM-dd hh:mm:ss')
    return 'N/A'
  }

  const handleEdit = (e, user) => {
    e.preventDefault()
    setUser(user)
    setOpen(true)
  }

  const handleDeleteClick = (e, user) => {
    e.preventDefault()
    e.stopPropagation()
    setUserToDelete(user)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteUser(userToDelete.id)
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  const handleSearch = () => {
    if (!searchKey.trim()) {
      setIsSearching(false)
      setSearchResults([])
      return
    }

    setIsSearching(true)

    // Filter the current allUsers data
    const filteredData = allUsers?.data?.filter(
      data =>
        data.email?.toLowerCase().includes(searchKey.toLowerCase()) ||
        data.firstName?.toLowerCase().includes(searchKey.toLowerCase()) ||
        data.lastName?.toLowerCase().includes(searchKey.toLowerCase())
    )

    setSearchResults(filteredData || [])
  }

  // Determine which data to display
  const totalDisplayed = isSearching
    ? searchResults.length
    : allUsers?.pagination?.total || 0

  useEffect(() => {
    setIsSearching(false)
    setSearchResults([])
  }, [allUsers])

  useEffect(() => {
    if (allUsers?.pagination?.page) {
      setCurrentPage(allUsers.pagination.page)
    }
  }, [allUsers])

  const displayData = isSearching ? searchResults : allUsers?.data || []

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className='font-semibold'>
                {userToDelete?.firstName} {userToDelete?.lastName}
              </span>{' '}
              ({userToDelete?.email})? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span className='flex items-center gap-2'>
                  <ClipLoader color='white' size={16} />
                  Deleting...
                </span>
              ) : (
                'Delete User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className='min-w-[650px]'>
          <SheetHeader>
            <SheetTitle>Edit User Profile</SheetTitle>
            <SheetDescription>
              <EditUser user={user} />
            </SheetDescription>
          </SheetHeader>
        </SheetContent>
      </Sheet>

      <div className='overflow-hidden'>
        <Card>
          <CardHeader>
            <CardTitle className='my-[10px]'>List of all users</CardTitle>
            <CardDescription className='flex gap-[10px] w-[60vw]'>
              <Input
                className='w-[80%]'
                placeholder='Enter user email or name here'
                value={searchKey}
                onChange={e => setSearchKey(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <Button onClick={handleSearch}>Search</Button>
              {isSearching && (
                <Button
                  variant='outline'
                  onClick={() => {
                    setIsSearching(false)
                    setSearchKey('')
                    setSearchResults([])
                  }}
                >
                  Clear Search
                </Button>
              )}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant='outline'>New User</Button>
                </SheetTrigger>
                <SheetContent className='min-w-[450px]'>
                  <SheetHeader>
                    <SheetTitle>New User Registration</SheetTitle>
                    <SheetDescription>
                      Enter the required user profile here. Click submit when
                      you're done.
                    </SheetDescription>
                  </SheetHeader>
                  <NewUser />
                </SheetContent>
              </Sheet>
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-2'>
            <Table>
              <TableCaption>
                {isSearching
                  ? `Search results for "${searchKey}" (${searchResults.length} users found)`
                  : 'A list of All Available Users'}
              </TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Name(s)</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className='text-right'>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && !isSearching && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className='flex justify-center'>
                        <ClipLoader />
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && displayData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className='text-center'>
                      {isSearching
                        ? `No users found for "${searchKey}"`
                        : 'No users available'}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  displayData.map(event => (
                    <TableRow key={event.id}>
                      <TableCell className='font-medium whitespace-nowrap'>
                        {event?.firstName} {event?.lastName}
                      </TableCell>
                      <TableCell>{event?.email}</TableCell>
                      <TableCell className='whitespace-break-spaces'>
                        {event?.role}
                      </TableCell>
                      <TableCell className='whitespace-nowrap'>
                        {handleDate(event?.createdAt)}
                      </TableCell>
                      <TableCell className='text-right'>
                        <div className='flex justify-end gap-2'>
                          <Button
                            variant='link'
                            onClick={e => handleEdit(e, event)}
                          >
                            Edit-user
                          </Button>
                          <Button
                            variant='link'
                            className='text-red-600 hover:text-red-800'
                            onClick={e => handleDeleteClick(e, event)}
                            disabled={isDeleting}
                          >
                            Delete User
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={5}>Total</TableCell>
                  <TableCell colSpan={1}>{totalDisplayed}</TableCell>
                </TableRow>
                {/* {!isSearching && allUsers?.pagination?.totalPages > 1 && (
                  <TableRow>
                    <TableCell colSpan={5}></TableCell>
                    <TableCell>
                      <div className='flex'>
                        <div className='justify-end'>
                          <Paginator
                            currentPage={currentPage}
                            disabled={isLoading}
                            onPageChange={onPageChange}
                            pageCount={allUsers?.pagination?.totalPages}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )} */}
              </TableFooter>
            </Table>
            <div className='flex items-center justify-between mt-6'>
              {/* Page size selector */}
              <div className='flex items-center gap-2 text-sm'>
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  className='border rounded px-2 py-1'
                >
                  {/* <option value={5}>5</option> */}
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                  <option value={50}>50</option>
                </select>
              </div>

              {/* Pagination info */}
              <div className='text-sm text-muted-foreground'>
                Page {allUsers?.pagination?.page} of{' '}
                {allUsers?.pagination?.totalPages} —{' '}
                {allUsers?.pagination?.total} users
              </div>

              {/* Prev / Next buttons */}
              <div className='flex gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </Button>

                <Button
                  variant='outline'
                  size='sm'
                  disabled={
                    currentPage === allUsers?.pagination?.totalPages ||
                    allUsers?.pagination?.totalPages === 0
                  }
                  onClick={() =>
                    setCurrentPage(prev =>
                      Math.min(
                        prev + 1,
                        allUsers?.pagination?.totalPages || prev
                      )
                    )
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
          <CardFooter></CardFooter>
        </Card>
      </div>
    </>
  )
}

export default User
